<script>
import { mapGetters } from 'vuex';
import {
  useUISettings,
  DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER,
  DEFAULT_CONVERSATION_CONTACT_ITEMS_ORDER,
} from 'dashboard/composables/useUISettings';
import {
  canReadResource,
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ContactAction from './ContactAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ConversationLabels from './labels/LabelBox.vue';
import ContactLabels from '../contacts/components/ContactLabels.vue';
import ContactRelatedCompanies from '../contacts/components/ContactRelatedCompanies.vue';

import ContactInfo from './contact/ContactInfo.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import draggable from 'vuedraggable';
import FlowsList from './Flows/List.vue';
import FlowRunHistory from './Flows/RunHistory.vue';
import PanelHeader from './sidebar/PanelHeader.vue';
import PanelSection from './sidebar/PanelSection.vue';
import PanelTabs from './sidebar/PanelTabs.vue';
import SocialProfiles from './contact/SocialProfiles.vue';

const CONVERSATION_TAB = 'conversation';

export default {
  components: {
    ContactConversations,
    ContactInfo,
    ContactLabels,
    ConversationInfo,
    ConversationLabels,
    CustomAttributes,
    ConversationAction,
    ContactAction,
    ConversationParticipant,
    ContactRelatedCompanies,
    DashboardIcon,
    Draggable: draggable,
    FlowsList,
    FlowRunHistory,
    PanelHeader,
    PanelSection,
    PanelTabs,
    SocialProfiles,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    onToggle: {
      type: Function,
      default: () => {},
    },
    isContactPanelDetached: {
      type: Boolean,
      default: false,
    },
    // Layout da tela (#1474). `viewport` e o legado, em que o painel decide por
    // `md` da janela: e o que o Kanban e a caixa de entrada seguem usando.
    layoutMode: {
      type: String,
      default: 'viewport',
    },
  },
  setup() {
    const {
      updateUISettings,
      conversationSidebarItemsOrder,
      conversationContactItemsOrder,
      conversationPanelActiveTab,
    } = useUISettings();

    return {
      updateUISettings,
      conversationSidebarItemsOrder,
      conversationContactItemsOrder,
      conversationPanelActiveTab,
    };
  },
  data() {
    return {
      dragEnabled: true,
      conversationItems: [],
      contactItems: [],
      dragging: false,
    };
  },
  computed: {
    // Nos layouts compacto e medio o painel abre POR CIMA da conversa e cobre o
    // botao de detalhes do cabecalho: o chevron de fechar daqui e a unica saida,
    // e travar o painel ao lado nao tem espaco para fazer nada.
    isPanelOverlay() {
      return this.layoutMode === 'compact' || this.layoutMode === 'medium';
    },
    canDetachPanel() {
      return !this.isPanelOverlay;
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    // A aba aberta NAO e estado local: ela vem do `ui_settings`, que e por
    // usuario e atravessa recarga, troca de conversa e troca de maquina.
    // Guardar aqui tambem faria as duas fontes divergirem no primeiro erro de
    // rede -- o store ja aplica a mudanca antes da resposta, entao a troca
    // continua instantanea lendo so daqui.
    activeTab() {
      return this.conversationPanelActiveTab;
    },
    isConversationTab() {
      return this.activeTab === CONVERSATION_TAB;
    },
    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },
    canShowFlowsSection() {
      return hasPermissions(
        ['administrator', 'automation_execute', 'automation_manage'],
        this.userPermissions
      );
    },
    canViewCustomAttributes() {
      return canReadResource(
        'customAttributeDefinitions',
        this.userPermissions
      );
    },
    // #1625: o contato chega pelo `meta.sender` da conversa, entao as secoes
    // dele aparecem mesmo para quem nao le contatos -- e as buscas delas
    // (`contacts/:id`, empresas relacionadas) tomariam 403.
    canViewContacts() {
      return canReadResource('contacts', this.userPermissions);
    },
    // Sem o catalogo de etiquetas as duas secoes de etiqueta ficam sem opcao
    // nenhuma para escolher e sem as etiquetas aplicadas (elas sao cruzadas
    // com o catalogo).
    canViewLabels() {
      return canReadResource('labels', this.userPermissions);
    },
    conversationAdditionalAttributes() {
      return {
        ...(this.currentChat.additional_attributes || {}),
        ...(this.currentConversationMetaData.additional_attributes || {}),
      };
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contactAdditionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    contactId() {
      return this.currentChat.meta?.sender?.id;
    },
    currentConversationMetaData() {
      return this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.conversationId);
    },
    participantsCount() {
      return (
        this.$store.getters['conversationWatchers/getByConversationId']?.(
          this.conversationId
        )?.length || 0
      );
    },
    contactAttributesCount() {
      return (
        this.$store.getters['attributes/getAttributesByModel']?.(
          'contact_attribute'
        )?.length || 0
      );
    },
    previousConversationsCount() {
      if (!this.contact?.id) {
        return 0;
      }
      const conversations =
        this.$store.getters['contactConversations/getContactConversation']?.(
          this.contact.id
        ) || [];
      return conversations.filter(
        conversation => conversation.id !== Number(this.conversationId)
      ).length;
    },
  },
  watch: {
    conversationId(newConversationId, prevConversationId) {
      if (newConversationId && newConversationId !== prevConversationId) {
        this.getContactDetails();
      }
    },
    contactId() {
      this.getContactDetails();
    },
    // O vuedraggable recalcula o mapa DOM -> item (`visibleIndexes`) só em três
    // momentos: no mount, quando a REFERÊNCIA da lista muda, e no fim de um
    // arraste. Nunca ao começar um.
    //
    // As seções que dependem do contato entram e saem da lista sem que a lista
    // mude de referência — o painel não desmonta ao trocar de conversa, então
    // `contacts/show` resolvendo faz cinco seções reaparecerem. Com o mapa
    // velho, o primeiro arraste depois disso solta o item uma posição fora, e o
    // `onDragEnd` grava a ordem errada. Trocar a referência dispara o watcher
    // interno e o recálculo.
    'contact.id': function watchContactId() {
      this.conversationItems = [...this.conversationItems];
      this.contactItems = [...this.contactItems];
    },
  },
  mounted() {
    this.syncItemsFromSettings();
    this.getContactDetails();
    if (this.canViewCustomAttributes) {
      this.$store.dispatch('attributes/get', 0);
    }
  },
  methods: {
    // Cria uma cópia mutável local para que o vuedraggable possa reordenar
    // sem tentar modificar o array frozen retornado pelo composable/Vuex.
    syncItemsFromSettings() {
      this.conversationItems = [...this.conversationSidebarItemsOrder];
      this.contactItems = [...this.conversationContactItemsOrder];
    },
    onPanelToggle() {
      this.onToggle();
    },
    getContactDetails() {
      if (this.contactId && this.canViewContacts) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    // O `v-if` fica no filho DIRETO do draggable, e não dentro do PanelSection:
    // assim a seção escondida não deixa um cartão vazio e arrastável na lista.
    // O `vuedraggable` sabe lidar com isso — ele mapeia índice de DOM para
    // índice de lista (`visibleIndexes`) —, mas só enquanto o `v-for` percorrer
    // exatamente o array passado em `:list`. Filtrar o `v-for` quebraria esse
    // mapeamento e o arraste passaria a mover o item errado.
    isSectionVisible(name) {
      // Quatro seções da aba de contato e a de conversas anteriores recebem o
      // id do contato numa prop `required`: sem contato carregado elas subiriam
      // com `undefined` e só se manifestariam como aviso no console.
      const needsContactId = [
        'previous_conversation',
        'contact_labels',
        'contact_assignment',
        'contact_attributes',
        'related_companies',
      ];

      if (needsContactId.includes(name) && !this.contact?.id) {
        return false;
      }
      if (name === 'flows') {
        return this.canShowFlowsSection;
      }
      if (name === 'contact_attributes') {
        return this.canViewCustomAttributes;
      }
      if (name === 'related_companies') {
        return this.canViewContacts;
      }
      if (name === 'conversation_labels' || name === 'contact_labels') {
        return this.canViewLabels;
      }
      return true;
    },
    sectionTitle(name) {
      const titles = {
        conversation_assignment: 'CONVERSATION_ASSIGNMENT',
        conversation_labels: 'CONVERSATION_LABELS',
        conversation_info: 'CONVERSATION_INFO',
        conversation_participants: 'PARTICIPANTS',
        flows: 'FLOWS',
        previous_conversation: 'PREVIOUS_CONVERSATIONS',
        contact_details: 'CONTACT_DETAILS',
        social_profiles: 'SOCIAL_PROFILES',
        contact_labels: 'CONTACT_LABELS',
        contact_assignment: 'CONTACT_ASSIGNMENT',
        contact_attributes: 'CONTACT_ATTRIBUTES',
        related_companies: 'RELATED_COMPANIES',
      };

      return this.$t(`CONVERSATION_SIDEBAR.SECTIONS.${titles[name]}`);
    },
    // Contagem discreta à direita do título de três seções (#1243). Seção sem
    // contagem conhecida (zero, ou o contato ainda não carregou) não renderiza
    // nada — número errado é pior que número ausente.
    sectionMeta(name) {
      if (name === 'conversation_participants') {
        const count = this.participantsCount;
        if (!count) return '';
        return count === 1
          ? this.$t('CONVERSATION_SIDEBAR.SECTION_META.PARTICIPANT')
          : this.$t('CONVERSATION_SIDEBAR.SECTION_META.PARTICIPANTS', {
              count,
            });
      }
      if (name === 'contact_attributes') {
        const count = this.contactAttributesCount;
        if (!count) return '';
        return count === 1
          ? this.$t('CONVERSATION_SIDEBAR.SECTION_META.CONTACT_ATTRIBUTE')
          : this.$t('CONVERSATION_SIDEBAR.SECTION_META.CONTACT_ATTRIBUTES', {
              count,
            });
      }
      if (name === 'previous_conversation') {
        const count = this.previousConversationsCount;
        return count ? String(count) : '';
      }
      return '';
    },
    onDragEnd() {
      this.dragging = false;
      this.updateUISettings(
        this.isConversationTab
          ? { conversation_sidebar_items_order: this.conversationItems }
          : {
              conversation_contact_sidebar_items_order: this.contactItems,
            }
      );
    },
    resetOrder() {
      if (this.isConversationTab) {
        this.conversationItems = [...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER];
        this.updateUISettings({
          conversation_sidebar_items_order: this.conversationItems,
        });
        return;
      }

      this.contactItems = [...DEFAULT_CONVERSATION_CONTACT_ITEMS_ORDER];
      this.updateUISettings({
        conversation_contact_sidebar_items_order: this.contactItems,
      });
    },
    // Cada seção do sidebar é um stacking context (relative + z-index inline),
    // com z DECRESCENTE na ordem visual: a de cima cobre a de baixo.
    //
    // Aqui existia um boost para o card de macros enquanto um popover estivesse
    // aberto. Não resolvia: o popover não era coberto, era RECORTADO por três
    // ancestrais com overflow, e recorte não se conserta com ordem de pintura.
    // Os popovers agora saem da árvore (Flows/PopoverPortal.vue) e o boost foi
    // embora com o problema.
    getSidebarItemZIndex(index, total) {
      return total - index;
    },
    onTabChange(tab) {
      this.updateUISettings({ conversation_panel_active_tab: tab });
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col h-full overflow-hidden bg-surface border-l text-body border-line-faint rtl:border-l-0 rtl:border-r contact--panel"
  >
    <!-- As duas linhas horizontais desta coluna tem de encontrar as da coluna
         da conversa, ao lado. Por isso a estrutura aqui e a mesma de la
         (ver o comentario de `.dashboard-app--tabs` em ConversationBox.vue):
         cabecalho SEM borda, e a faixa das abas com borda em cima e embaixo.

         Nao e detalhe: com a borda no cabecalho, a primeira linha caia no
         ultimo pixel DENTRO da faixa de titulo, enquanto do outro lado ela e o
         primeiro pixel DEPOIS dela. Um pixel de diferenca -- que e exatamente o
         que se via.

         As alturas vem dos mesmos tokens dos dois lados: `min-h-title-band` no
         cabecalho e `min-h-header-band` na faixa das abas, e a cor da borda e a
         mesma (`border-line`): linha que encontra outra mais fraca ao lado
         parece quebrada, nao continuada.

         A cor tambem: o cabecalho nao pinta fundo nenhum e fica com o
         `bg-surface` da coluna, que e o mesmo token do cabecalho da conversa
         (`ConversationHeader.vue`). Era `bg-surface-sunken`, e as duas faixas
         ficavam lado a lado em cinzas diferentes. -->
    <div class="shrink-0">
      <div class="flex items-center px-3 min-h-title-band">
        <PanelHeader
          :contact="contact"
          :is-contact-panel-detached="isContactPanelDetached"
          :show-detach-button="canDetachPanel"
          :close-always-visible="isPanelOverlay"
          @contactPanelDetachToggle="$emit('contactPanelDetachToggle')"
          @togglePanel="onPanelToggle"
        />
      </div>
      <div
        class="flex items-center px-2 border-y min-h-header-band border-line"
      >
        <PanelTabs class="w-full" :active="activeTab" @change="onTabChange" />
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
      <!-- `v-show`, e não `v-if`: trocar de aba não pode desmontar a árvore.
           Desmontar faria os Fluxos, as conversas anteriores e as empresas
           relacionadas buscarem de novo a cada clique numa aba, e apagaria o
           atalho `L` do LabelBox, que antes ficava montado o tempo todo porque
           o AccordionItem colapsava com `v-show`.

           O `role="tabpanel"` mora neste `<div>`, e não no `<Draggable>`, porque
           o vuedraggable trata TODO `$attrs` como opção do Sortable e não
           repassa nada para o DOM. Posto nele, `id`/`role`/`aria-labelledby`
           viravam opções inexistentes do Sortable e sumiam da página — o
           `aria-controls` das abas apontaria para um id que não existe. -->
      <div
        v-show="isConversationTab"
        id="conversation-panel-tabpanel-conversation"
        role="tabpanel"
        tabindex="0"
        aria-labelledby="conversation-panel-tab-conversation"
      >
        <Draggable
          :list="conversationItems"
          :disabled="!dragEnabled"
          animation="200"
          class="flex flex-col"
          ghost-class="ghost"
          handle=".drag-handle"
          @start="dragging = true"
          @end="onDragEnd"
        >
          <transition-group>
            <template v-for="(element, index) in conversationItems">
              <div
                v-if="isSectionVisible(element.name)"
                :key="element.name"
                class="relative border-b border-line-faint last:border-b-0"
                :style="{
                  zIndex: getSidebarItemZIndex(index, conversationItems.length),
                }"
              >
                <PanelSection
                  draggable
                  :title="sectionTitle(element.name)"
                  :meta="sectionMeta(element.name)"
                  :class="
                    element.name === 'conversation_assignment'
                      ? 'conversation--actions'
                      : ''
                  "
                >
                  <ConversationAction
                    v-if="element.name === 'conversation_assignment'"
                    :conversation-id="conversationId"
                    :inbox-id="inboxId"
                  />
                  <ConversationLabels
                    v-else-if="element.name === 'conversation_labels'"
                  />
                  <ConversationInfo
                    v-else-if="element.name === 'conversation_info'"
                    :conversation-attributes="conversationAdditionalAttributes"
                    :contact-attributes="contactAdditionalAttributes"
                  />
                  <ConversationParticipant
                    v-else-if="element.name === 'conversation_participants'"
                    :conversation-id="conversationId"
                  />
                  <!-- Sem <woot-feature-toggle>: a aba de Fluxos é o ÚNICO lugar
                   onde o atendente dispara um fluxo manual, e desde a #728 o
                   motor deixou de ser gateado por `automation_flows_engine`
                   justamente porque o bit pode ser zerado em silêncio (o form do
                   super admin reescreve o bitmask inteiro). Gatear a aba por
                   QUALQUER bit de conta — `macros`, que acabou de sair, ou o bit
                   do motor — a faria sumir sem erro, sem console e sem teste
                   vermelho. O gate agora é só de permissão (canShowFlowsSection,
                   já em automation_*).

                   #913: o item chama-se `flows`. O nome antigo (`macros`) não
                   aparece mais aqui de propósito — quem traduz o que está
                   gravado no usuário é o `useUISettings`, num lugar só
                   (LEGACY_SIDEBAR_ITEM_NAMES). -->
                  <FlowsList
                    v-else-if="element.name === 'flows'"
                    :conversation-id="conversationId"
                  />
                  <ContactConversations
                    v-else-if="element.name === 'previous_conversation'"
                    :contact-id="contact.id"
                    :conversation-id="conversationId"
                  />
                </PanelSection>
              </div>
            </template>
          </transition-group>
        </Draggable>
        <!-- Fluxos que já rodaram nesta conversa (#1705). FORA do Draggable de
             propósito: é a última posição da aba, sempre, e não entra na ordem
             que o usuário arrasta e salva. -->
        <div class="border-t border-line-faint">
          <PanelSection :title="$t('AUTOMATION_FLOWS.RUN_HISTORY.TITLE')">
            <FlowRunHistory
              :conversation-id="conversationId"
              :active="isConversationTab"
            />
          </PanelSection>
        </div>
      </div>

      <div
        v-show="!isConversationTab"
        id="conversation-panel-tabpanel-contact"
        role="tabpanel"
        tabindex="0"
        aria-labelledby="conversation-panel-tab-contact"
      >
        <Draggable
          :list="contactItems"
          :disabled="!dragEnabled"
          animation="200"
          class="flex flex-col"
          ghost-class="ghost"
          handle=".drag-handle"
          @start="dragging = true"
          @end="onDragEnd"
        >
          <transition-group>
            <template v-for="(element, index) in contactItems">
              <div
                v-if="isSectionVisible(element.name)"
                :key="element.name"
                class="relative border-b border-line-faint last:border-b-0"
                :style="{
                  zIndex: getSidebarItemZIndex(index, contactItems.length),
                }"
              >
                <PanelSection
                  draggable
                  :title="sectionTitle(element.name)"
                  :meta="sectionMeta(element.name)"
                >
                  <ContactInfo
                    v-if="element.name === 'contact_details'"
                    :contact="contact"
                    :show-avatar="false"
                    :show-close-button="false"
                    :show-detach-button="false"
                    compact
                    @panelClose="onPanelToggle"
                  />
                  <SocialProfiles
                    v-else-if="element.name === 'social_profiles'"
                    :contact="contact"
                  />
                  <ContactLabels
                    v-else-if="element.name === 'contact_labels'"
                    :contact-id="contact.id"
                  />
                  <ContactAction
                    v-else-if="element.name === 'contact_assignment'"
                    :contact-id="contact.id"
                  />
                  <CustomAttributes
                    v-else-if="element.name === 'contact_attributes'"
                    attribute-type="contact_attribute"
                    attribute-from="conversation_contact_panel"
                    :contact-id="contact.id"
                    :empty-state-message="
                      $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                    "
                    panel
                  />
                  <ContactRelatedCompanies
                    v-else-if="element.name === 'related_companies'"
                    :contact-id="contact.id"
                    :show-edit-action="false"
                  />
                </PanelSection>
              </div>
            </template>
          </transition-group>
        </Draggable>
        <!-- Fluxos que já rodaram para este contato (#1705), em qualquer
             conversa dele. Mesma regra da aba Conversa: última posição, fora da
             ordem arrastável. Só busca quando a aba Contato fica visível.
             O endpoint é do CONTATO (`ContactPolicy#show?`): sem leitura de
             contatos ele responde 403, então a seção some, como as empresas
             relacionadas (#1625). -->
        <div
          v-if="contact.id && canViewContacts"
          class="border-t border-line-faint"
        >
          <PanelSection :title="$t('AUTOMATION_FLOWS.RUN_HISTORY.TITLE')">
            <FlowRunHistory
              :contact-id="contact.id"
              :active="!isConversationTab"
            />
          </PanelSection>
        </div>
      </div>

      <!-- Ultima faixa da coluna, DENTRO do scroll e FORA das duas listas
           arrastaveis. Fora delas porque ela age sobre a aba ativa: como item
           da lista, seria um cartao arrastavel que restaura a ordem da lista
           em que ele mesmo esta, duplicado nas duas abas. Sem titulo pelo
           mesmo motivo -- nao e uma categoria de informacao, e uma acao sobre
           a lista.

           Dentro do scroll porque fixa ela cobrava altura da coluna o tempo
           todo, e o painel e a parte mais apertada da tela. Rola junto e e
           sempre a ultima, porque vem depois dos dois `<Draggable>` na
           arvore -- nao ha ordem que a mova. -->
      <div class="flex justify-center px-4 py-3 border-t border-line-faint">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors duration-150 border rounded-lg text-muted border-line bg-surface-sunken dark:bg-surface-raised hover:bg-line-faint dark:hover:bg-surface-hover hover:text-body"
          @click="resetOrder"
        >
          <DashboardIcon icon="arrow-clockwise-outline" size="14" />
          <span>{{ $t('CONVERSATION_SIDEBAR.PANEL_TABS.RESET_ORDER') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// A etiqueta já cadastrada ganha o mesmo cinza dos seletores e dos botões do
// painel. `.label.smooth` nasce `bg-transparent` (o que a distingue das outras
// variantes é a borda), e no meio de uma coluna onde toda superfície clicável
// usa `surface-sunken` ela lia como um contorno solto no branco.
//
// Estas duas regras moram FORA do bloco `::v-deep` abaixo por causa da
// especificidade, e não por estilo. Dentro dele o seletor sairia
// `[data-v-x] .label.smooth` — (0,3,0), EMPATE com o `.label.smooth[data-v-y]`
// do próprio `Label.vue`, e quem ganha passa a depender da ordem dos bundles.
// Escrito assim, o atributo de escopo cai no `.contact--panel` e a conta sobe
// para (0,4,0). A segunda regra existe porque no tema escuro o `Label.vue`
// também sobe: `.dark .label.smooth[data-v-y]` volta a empatar.
.contact--panel ::v-deep .label.smooth {
  @apply bg-surface-sunken;
}

.dark .contact--panel ::v-deep .label.smooth {
  @apply bg-surface-sunken;
}

// A FORMA e as CORES do botao de acrescentar etiqueta -- o unico do painel que
// nao e o `PanelAddButton`: ele vem do `shared/.../AddLabel.vue`, que a pagina
// de contatos, o perfil da empresa e o assistente de campanha tambem montam.
// A forma da referencia entra por aqui, onde alcanca so o painel.
//
// `.label--add.button`, e nao so `.label--add`: o `woot-button
// variant="smooth"` pinta fundo e cor por `.button.smooth`, dois nomes de
// classe -- com um nome so a regra nasce fraca demais. `!important` nao e
// saida: o SCSS nao aceita o `!` dentro de `@apply`.
//
// As medidas sao as do chip que fica ao lado: `h-6`, `text-xs`, `font-medium`,
// `px-2.5`, `gap-1.5` -- o tamanho CHEIO do `components/ui/Label.vue`, que e o
// que as duas abas do painel montam (`label smooth`, sem `small`). Medido nas
// duas: 24px e 12px. Ja errei isto uma vez medindo o chip errado: existe um
// `label smooth small` de 20px na tela, mas ele e do CARTAO DA LISTA de
// conversas, fora do painel.
//
// Elas moram aqui fora pelo mesmo motivo das duas regras acima, e o defeito
// que isso corrige era visivel: no tema ESCURO o botao nascia azul -- a aparencia que deveria ser a
// do hover -- e ao passar o mouse virava azul CHEIO.
//
// Medido em 21/09/2026 no CSS ja compilado. Dentro do `::v-deep` o seletor sai
// `[data-v-x] .label--add.button`, (0,3,0). O `woot-button variant="smooth"`
// do `AddLabel.vue` traz `dark:bg-brand-subtle` e `dark:text-brand-text`, que o
// Tailwind compila para `:is(.dark .button.smooth)` -- e o `:is()` assume a
// especificidade do argumento mais forte, tambem (0,3,0). Empate: decide a
// ordem dos bundles, e quem ganhava era o global.
//
// No tema CLARO o concorrente e so `.button.smooth`, (0,2,0), e a regra de
// dentro ganhava -- por isso o defeito nao aparecia la. Escrito aqui fora, o
// atributo de escopo cai no `.contact--panel` e a conta sobe para (0,4,0),
// acima dos dois, nos dois temas.
.contact--panel ::v-deep .label--add.button {
  @apply h-6 gap-1.5 px-2.5 py-0 text-xs font-medium border border-dashed
    rounded-full shadow-none bg-transparent text-muted border-line-strong;

  &:hover {
    @apply bg-brand/5 text-brand-text border-brand;
  }
}

::v-deep {
  // O estilo do gatilho dos seletores (`.panel-selector`) mora em
  // `assets/scss/widgets/_panel-selector.scss`: o `ContactAction` tambem aparece
  // no painel de contato, fora deste componente.

  // O recuo lateral e a escala de texto do campo personalizado moram agora no
  // proprio `CustomAttribute.vue`, na variante `panel` (#1243). O que sobra
  // aqui e o respiro do PRIMEIRO da lista: o `py-2.5` dele somaria a margem de
  // baixo do titulo da secao e abriria um vao maior que o das outras secoes.
  .custom-attribute:first-child {
    @apply pt-0;
  }

  // `align-middle` nos DOIS, e nao so no botao. Os dois sao `inline-flex` na
  // mesma linha de texto, e o alinhamento padrao e pela LINHA DE BASE -- que,
  // num inline-flex, e a do primeiro filho. No chip o primeiro filho e o ponto
  // colorido (um bloco sem texto, cuja base e a borda de baixo); no botao e o
  // icone `+`. Duas bases diferentes, dois topos diferentes, e nenhuma
  // diferenca de altura para explicar o degrau.
  .label,
  .label--add.button {
    @apply align-middle;
  }

  // A margem de BAIXO tem de ser a mesma do chip (4px): `vertical-align:
  // middle` centra a caixa de MARGEM, nao a de borda, entao 2px contra 4px
  // deixava o botao 1px mais baixo mesmo com os dois medindo 24px.
  //
  // A da DIREITA sai. Entre o botao e o primeiro chip ja existe o espaco em
  // branco do HTML, que em contexto de linha vira um espaco de verdade -- a
  // margem so o dobrava.
  .label--add.button {
    @apply mb-1 mr-0;
  }

  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }
    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}

.custom-scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(148, 163, 184, 0.7);
    }
  }
}
</style>
